import { TooltipDirective } from '../tooltip/tooltip.directive';
import { BOTONES } from '../../componentes/botones';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CamarasService } from '../servicios/http/camara.service';
import { livePeerPool } from './live-peer-pool';

/**
 * Player unificado de cámara. Sustituye a todos los wrappers anteriores
 * (`video-dahua-p2p`, `videogular`, `mosaic-video`). Único punto donde se
 * llama a `camarasService.live()` / `.record()` y donde se monta el `<video>`.
 *
 * Funcionamiento:
 *   1. Recibe `idCamara` + `canal` (+ `fecha` opcional para playback)
 *   2. Llama a la API pidiendo `tipoStream=webrtc`
 *   3. Recibe `{stream, webrtc: true}` — la URL es WHEP de go2rtc
 *   4. Negocia `RTCPeerConnection` y muestra el video
 *
 * Si el backend devuelve `webrtc: false` por algún motivo (cámara cuyo tipo
 * todavía no se migró a go2rtc), mostramos error claro — el flag webrtc es
 * la nueva contract.
 */

/** True si el browser puede recibir H.265 en WebRTC.
 * Chrome/Edge Windows y Mac (>= 122 + hardware) lo soportan.
 * Linux y Firefox no. Si la cámara emite H.265 y el cliente no lo soporta,
 * go2rtc devuelve 500 ("codecs not matched") en signaling. */
function browserSupportsH265(): boolean {
  try {
    const caps = RTCRtpReceiver.getCapabilities('video');
    if (!caps?.codecs) return false;
    return caps.codecs.some((c) => {
      const mime = c.mimeType.toLowerCase();
      return mime === 'video/h265' || mime === 'video/hevc';
    });
  } catch {
    return false;
  }
}

/** Códigos internos de error del player. El texto para el usuario vive en
 * `handleError()`; `errorEmiter` emite el código. */
type PlayerErrorCode =
  | 'CAMERA_OFFLINE'
  | 'CAMERA_NO_VIDEO'
  | 'CAMERA_AUTH_ERROR'
  | 'CAMERA_CONNECTING'
  | 'CAMERA_REJECTED'
  | 'STREAM_STALLED'
  | 'TUNNEL_ERROR'
  | 'SERVER_BUSY'
  | 'NO_H265_SUPPORT'
  | 'STREAM_INCOMPATIBLE'
  | 'ICE_FAILED'
  | 'SIGNALING_ERROR';

const RETRYABLE_ERRORS: ReadonlySet<PlayerErrorCode> = new Set([
  'SERVER_BUSY',
  'ICE_FAILED',
  'CAMERA_CONNECTING',
  'TUNNEL_ERROR',
  'STREAM_STALLED',
]);

/** Errores del equipo que no cambian en segundos: se muestran y se aplica el
 * cooldown de OFFLINE_COOLDOWN_MS para no volver a pegarle al túnel en cada
 * remontaje del player. */
const COOLDOWN_ERRORS: ReadonlySet<PlayerErrorCode> = new Set([
  'CAMERA_OFFLINE',
  'CAMERA_NO_VIDEO',
  'CAMERA_AUTH_ERROR',
  'CAMERA_REJECTED',
]);

/** api-camaras devuelve el motivo como `message` del body ({message,
 * statusCode}); Angular lo deja en `HttpErrorResponse.error`, mientras que
 * `.message` es el genérico "Http failure response for ...". */
function errorCode(err: unknown): string {
  const body = (err as { error?: { message?: unknown } })?.error;
  if (typeof body?.message === 'string' && body.message) return body.message;
  return (err as Error)?.message ?? String(err);
}

/** Segundos que el túnel dice que dura su bloqueo (NO_VIDEO, credenciales,
 * límite de logins), si api-camaras lo mandó. */
function errorRetryAfterMs(err: unknown): number | undefined {
  const secs = (err as { error?: { retryAfterSecs?: unknown } })?.error
    ?.retryAfterSecs;
  return typeof secs === 'number' && secs > 0 ? secs * 1000 : undefined;
}

/** Clasifica el error de signaling WHEP leyendo el body que devuelve go2rtc
 * (ahí viene el error real del source: RTSP 404 del DVR, timeout del ffmpeg
 * de transcode, mismatch de códecs, etc). Nunca asumir códec por el status:
 * un 500 puede ser cualquiera de esas causas. */
function classifyWhepError(body: string): PlayerErrorCode {
  // El ffmpeg de transcode se rindió antes de recibir el primer frame (DoLynk
  // puede tardar 20-30 s en arrancar). Es transitorio. Va primero: el body
  // trae `rtsp://127.0.0.1:8554/<hash hex>` y un hash con "401"/"404" caía
  // en los casos de abajo.
  if (/output file does not contain any stream/i.test(body)) {
    return 'CAMERA_CONNECTING';
  }
  // 401 del equipo con credenciales que hasta hace un momento andaban: los
  // Dahua lo devuelven cuando la cuenta quedó bloqueada temporalmente o se
  // superó su tope de sesiones. Reintentar lo empeora.
  if (/401|unauthorized/i.test(body)) return 'CAMERA_REJECTED';
  if (/404|not found|describe/i.test(body)) return 'CAMERA_OFFLINE';
  // "Invalid data found" es lo que dice ffmpeg cuando el equipo le cierra la
  // conexión sin responder: pasa justo después de cerrar otras sesiones del
  // mismo DVR (cambio de layout del mosaico) y se resuelve en segundos.
  if (
    /timeout|broken pipe|EOF|connection re(set|fused)|invalid data/i.test(body)
  ) {
    return 'SERVER_BUSY';
  }
  if (/codecs? (not )?match|codec/i.test(body)) {
    return browserSupportsH265() ? 'STREAM_INCOMPATIBLE' : 'NO_H265_SUPPORT';
  }
  return 'SIGNALING_ERROR';
}

const ERROR_MESSAGES: Record<string, string> = {
  CAMERA_OFFLINE: 'La cámara o el canal no están disponibles (sin señal).',
  CAMERA_NO_VIDEO:
    'La cámara responde pero no está entregando video. Revisá el equipo.',
  CAMERA_AUTH_ERROR:
    'El equipo rechazó el usuario o la contraseña cargados para la cámara.',
  CAMERA_CONNECTING:
    'Conectando con la cámara, puede tardar unos segundos. Reintentando…',
  TUNNEL_ERROR: 'No se pudo establecer la conexión P2P con la cámara.',
  STREAM_STALLED: 'Se cortó el video de la cámara.',
  CAMERA_REJECTED:
    'El equipo rechazó la conexión: cuenta bloqueada temporalmente o demasiadas sesiones abiertas. Esperá unos minutos.',
  SERVER_BUSY: 'El servidor de video está ocupado. Probá de nuevo.',
  NO_H265_SUPPORT:
    'Tu navegador no soporta H.265. Usá Chrome o Edge en Windows o Mac.',
  STREAM_INCOMPATIBLE: 'El formato del stream no es compatible.',
  ICE_FAILED:
    'No se pudo conectar al stream. Si estás en Firefox, probá Chrome o Edge.',
  SIGNALING_ERROR: 'No se pudo iniciar el stream.',
};

/** Throttle global de console.error: máx 1 log por cámara+canal+código cada
 * 30s. Sin esto, una grilla con canales caídos floodea la consola en cada
 * remontaje/navegación. */
const LOG_THROTTLE_MS = 30_000;
const lastLogByKey = new Map<string, number>();
function throttledLogError(key: string, ...args: unknown[]): void {
  const now = Date.now();
  const last = lastLogByKey.get(key) ?? 0;
  if (now - last < LOG_THROTTLE_MS) return;
  lastLogByKey.set(key, now);
  console.error(...args);
}

/** Cooldown de canales sin señal (compartido entre instancias): al navegar
 * mosaico/páginas los players se remontan y, sin esto, cada remontaje
 * re-POSTea el WHEP de un canal muerto — un 500 en la consola (Chrome lo
 * loguea siempre, no se puede suprimir desde JS) más el error propio. Dentro
 * del cooldown el player muestra el error directo sin tocar la red. */
const OFFLINE_COOLDOWN_MS = 30_000;
const offlineUntil = new Map<
  string,
  { until: number; code: PlayerErrorCode }
>();
@Component({
  selector: 'app-camera-player',
  templateUrl: './camera-player.component.html',
  styleUrls: ['./camera-player.component.scss'],
  standalone: true,
  imports: [TooltipDirective, BOTONES, 
    MatIconModule,
    
    MatProgressSpinnerModule,
  ],
})
export class CameraPlayerComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  private camarasService = inject(CamarasService);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  @ViewChild('videoEl', { static: true })
  videoEl!: ElementRef<HTMLVideoElement>;

  @Input() idCamara?: string;
  @Input() canal?: string;
  /** Si está seteada, se reproduce grabación (record), no live. */
  @Input() fecha?: string;
  /** main vs sub. Default true = main. */
  @Input() mainStream: boolean = true;
  @Input() encryptionKey?: string;
  @Input() botonMultiple?: boolean = false;
  @Input() maximizado?: boolean = false;
  @Output() maximizadoEmiter = new EventEmitter<boolean>();
  @Output() errorEmiter = new EventEmitter<unknown>();

  private pc: RTCPeerConnection | null = null;
  private mediaStream: MediaStream | null = null;
  /** Clave de livePeerPool de la conexión actual (null = grabación). Se
   * guarda al abrirla: en ngOnChanges los inputs ya traen los valores nuevos. */
  private peerKey: string | null = null;
  public loading = true;
  public errorMsg: string | null = null;
  /** True cuando el error es transitorio y ya no quedan reintentos:
   * habilita el botón "Reintentar" manual. */
  public puedeReintentar = false;

  private static readonly MAX_RETRIES = 2;
  private retryCount = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  /** Reintento lento, después de agotar los rápidos o ante errores del
   * equipo: un mosaico queda abierto horas y un corte cualquiera no puede
   * dejar la celda muerta. Espaciado y con jitter para no mandarle una
   * ráfaga al DVR cuando fallan varias celdas juntas. El freno fuerte (que
   * protege al equipo de logins repetidos) está en el túnel; acá alcanza con
   * no martillar la API. */
  private static readonly SLOW_RETRY_MS = [5_000, 10_000, 15_000, 30_000];
  private slowRetryCount = 0;
  /** Segundos hasta el próximo reintento automático (para el overlay). */
  public retryInSecs: number | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private onVisible: (() => void) | null = null;

  /** Detección de video congelado: la conexión sigue viva pero no llegan
   * frames (el túnel se quedó mudo, go2rtc perdió el producer). */
  private static readonly STALL_MS = 20_000;
  private stallTimer: ReturnType<typeof setInterval> | null = null;
  private lastFrames = -1;
  private lastFrameAt = 0;
  private disconnectedTimer: ReturnType<typeof setTimeout> | null = null;

  /** Pausa por no estar a la vista: pestaña oculta / ventana minimizada, o
   * el player fuera de pantalla (scroll). Mantener el stream abierto ahí no
   * lo ve nadie y ocupa un ffmpeg en go2rtc y una sesión RTSP en el equipo.
   * Se espera PAUSE_AFTER_MS para no cortar y reconectar en cada cambio
   * rápido de pestaña; al volver a la vista se reconecta solo. Las
   * grabaciones no se pausan: al reconectar arrancarían otra vez desde la
   * fecha pedida y se perdería por dónde iba. */
  private static readonly PAUSE_AFTER_MS = 30_000;
  public paused = false;
  private inView = true;
  private pauseTimer: ReturnType<typeof setTimeout> | null = null;
  private intersection: IntersectionObserver | null = null;
  private readonly onPageVisibility = () => this.updateVisibility();
  /** Se incrementa en cada stop(): una carga que estaba esperando a la API
   * cuando se pausó, se destruyó el player o cambiaron los inputs no debe
   * seguir y abrir un WebRTC que ya nadie cierra (quedaba un consumer
   * huérfano en go2rtc manteniendo el canal abierto). */
  private loadSeq = 0;

  // PTZ
  public ptzVisible = false;
  public ptzBusy = false;
  public ptzSupported = true;
  private ptzInterval: ReturnType<typeof setInterval> | null = null;

  ngAfterViewInit(): void {
    this.watchVisibility();
    // Jitter inicial: desincroniza los POST WHEP de una grilla que monta
    // 4-9 players a la vez (evita el pico simultáneo de sources en go2rtc).
    // Sin jitter si hay una conexión en espera para adoptar: no va a POSTear.
    if (this.idCamara) {
      const delay =
        !this.fecha && livePeerPool.has(this.liveKey())
          ? 0
          : Math.random() * 400;
      this.retryTimer = setTimeout(() => void this.load(), delay);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['idCamara'] && !changes['idCamara'].firstChange) ||
      (changes['canal'] && !changes['canal'].firstChange) ||
      (changes['fecha'] && !changes['fecha'].firstChange) ||
      (changes['mainStream'] && !changes['mainStream'].firstChange)
    ) {
      this.stop(true);
      this.retryCount = 0;
      this.slowRetryCount = 0;
      // Pausado: carga al volver a la vista, ya con los inputs nuevos.
      if (changes['fecha'] && this.fecha) this.paused = false;
      if (this.idCamara && !this.paused) void this.load();
      this.updateVisibility();
    }
  }

  ngOnDestroy(): void {
    this.stop(true);
    void this.stopPTZ();
    this.clearPauseTimer();
    this.intersection?.disconnect();
    this.intersection = null;
    document.removeEventListener('visibilitychange', this.onPageVisibility);
  }

  private watchVisibility(): void {
    document.addEventListener('visibilitychange', this.onPageVisibility);
    if (typeof IntersectionObserver !== 'undefined') {
      this.intersection = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        this.inView = !!entry?.isIntersecting;
        this.updateVisibility();
      });
      this.intersection.observe(this.host.nativeElement);
    }
  }

  /** Arranca/cancela la espera para pausar, o reanuda si volvió a la vista. */
  private updateVisibility(): void {
    const visible = !document.hidden && this.inView;
    if (visible) {
      this.clearPauseTimer();
      if (this.paused) this.resume();
      return;
    }
    if (this.paused || this.pauseTimer || this.fecha || !this.idCamara) {
      return;
    }
    this.pauseTimer = setTimeout(() => {
      this.pauseTimer = null;
      if (!document.hidden && this.inView) return;
      if (this.fecha) return;
      this.stop();
      this.paused = true;
      this.loading = true; // al volver se ve "cargando", no el último frame
      this.errorMsg = null;
      this.puedeReintentar = false;
    }, CameraPlayerComponent.PAUSE_AFTER_MS);
  }

  private resume(): void {
    this.paused = false;
    this.retryCount = 0;
    this.slowRetryCount = 0;
    if (this.idCamara) void this.load();
  }

  private clearPauseTimer(): void {
    if (this.pauseTimer) {
      clearTimeout(this.pauseTimer);
      this.pauseTimer = null;
    }
  }

  /** Botón "Reintentar" del template: resetea el presupuesto de reintentos
   * y limpia el cooldown de offline para forzar un intento real. */
  reintentar(): void {
    this.stop();
    this.retryCount = 0;
    this.slowRetryCount = 0;
    offlineUntil.delete(`${this.idCamara}:${this.canal}`);
    void this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.errorMsg = null;
    this.puedeReintentar = false;

    if (!this.idCamara) {
      this.handleError('idCamara requerido');
      return;
    }

    // Canal marcado sin señal hace <60s: mostrar el error sin tocar la red
    // (los remontajes por navegación de mosaico/páginas no re-POSTean).
    const offlineKey = `${this.idCamara}:${this.canal}`;
    const cooldown = offlineUntil.get(offlineKey);
    if (!this.fecha && cooldown && cooldown.until > Date.now()) {
      this.errorMsg = ERROR_MESSAGES[cooldown.code];
      this.puedeReintentar = true;
      this.loading = false;
      this.scheduleSlowRetry(cooldown.until - Date.now());
      return;
    }

    if (!this.fecha && this.adoptParked()) return;

    const seq = this.loadSeq;
    try {
      const res = this.fecha
        ? await this.camarasService.record(
            this.idCamara,
            this.canal ?? '0',
            this.fecha,
          )
        : await this.camarasService.live(
            this.idCamara,
            this.canal ?? '0',
            this.mainStream ? 'main' : 'sub',
            this.encryptionKey,
          );
      if (seq !== this.loadSeq) return;

      const url =
        (res as { stream?: string; mainStream?: string }).stream ??
        (res as { mainStream?: string }).mainStream;
      const isWebrtc = (res as { webrtc?: boolean }).webrtc === true;
      if (!url) {
        this.handleError('Backend no devolvió URL de stream');
        return;
      }
      if (!isWebrtc) {
        this.handleError(
          'Backend devolvió URL no-WebRTC. Tipo de cámara no soportado por el player unificado.',
        );
        return;
      }
      await this.startWebRTC(url, this.fecha ? null : this.liveKey());
    } catch (err) {
      if (seq !== this.loadSeq) return;
      this.handleError(errorCode(err), errorRetryAfterMs(err));
    }
  }

  private liveKey(): string {
    return `${this.idCamara}:${this.canal ?? '0'}:${this.mainStream ? 'main' : 'sub'}`;
  }

  /** Reusa la conexión en vivo que dejó en espera un player anterior de la
   * misma cámara+canal+calidad (ver livePeerPool). */
  private adoptParked(): boolean {
    const key = this.liveKey();
    const peer = livePeerPool.take(key);
    if (!peer) return false;
    this.pc = peer.pc;
    this.mediaStream = peer.stream;
    this.peerKey = key;
    this.bindPeer(peer.pc, peer.stream);
    this.attachStream(peer.stream);
    return true;
  }

  private async startWebRTC(
    streamUrl: string,
    key: string | null,
  ): Promise<void> {
    // Cerrar PC anterior si quedó viva (load() llamado en paralelo).
    if (this.pc) {
      try {
        this.pc.close();
      } catch {
        /* ignore */
      }
      this.pc = null;
    }
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      bundlePolicy: 'max-bundle',
    });
    const stream = new MediaStream();
    this.pc = pc;
    this.mediaStream = stream;
    this.peerKey = key;

    const closeIfStale = (): boolean => {
      if (this.pc === pc && pc.signalingState !== 'closed') return false;
      try {
        pc.close();
      } catch {
        /* ignore */
      }
      return true;
    };

    pc.addTransceiver('video', { direction: 'recvonly' });
    pc.addTransceiver('audio', { direction: 'recvonly' });
    this.bindPeer(pc, stream);

    const offer = await pc.createOffer();
    if (closeIfStale()) return;
    await pc.setLocalDescription(offer);
    if (closeIfStale()) return;

    const res = await fetch(streamUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp' },
      body: offer.sdp ?? '',
    });
    if (closeIfStale()) return;
    if (!res.ok) {
      const body = await res.text().catch((): string => '');
      throw new Error(classifyWhepError(body));
    }
    const answerSdp = await res.text();
    if (closeIfStale()) return;
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
  }

  /** Handlers de la conexión para este player. Al adoptar una conexión en
   * espera se vuelven a enganchar: los del player anterior quedan stale. */
  private bindPeer(pc: RTCPeerConnection, stream: MediaStream): void {
    const isStale = (): boolean =>
      this.pc !== pc || pc.signalingState === 'closed';

    pc.ontrack = (ev) => {
      if (isStale()) return;
      if (ev.streams.length > 0) {
        for (const track of ev.streams[0].getTracks()) stream.addTrack(track);
      } else {
        stream.addTrack(ev.track);
      }
      this.attachStream(stream);
    };

    pc.oniceconnectionstatechange = () => {
      if (isStale()) return;
      const state = pc.iceConnectionState;
      if (state === 'failed') {
        this.handleError('ICE_FAILED');
        return;
      }
      // 'disconnected' puede volver solo; si dura, se trata como caída.
      if (state === 'disconnected') {
        if (!this.disconnectedTimer) {
          this.disconnectedTimer = setTimeout(() => {
            this.disconnectedTimer = null;
            if (!isStale() && pc.iceConnectionState === 'disconnected') {
              this.handleError('ICE_FAILED');
            }
          }, 8_000);
        }
      } else if (this.disconnectedTimer) {
        clearTimeout(this.disconnectedTimer);
        this.disconnectedTimer = null;
      }
    };
    pc.onconnectionstatechange = () => {
      if (isStale()) return;
      if (pc.connectionState === 'failed') {
        this.handleError('ICE_FAILED');
      }
    };
  }

  private attachStream(stream: MediaStream): void {
    const video = this.videoEl.nativeElement;
    if (video.srcObject !== stream) video.srcObject = stream;
    void video.play().catch((): void => undefined);
    this.loading = false;
    this.retryCount = 0;
    this.slowRetryCount = 0;
    this.startStallWatch();
  }

  /** `retryAfterMs`: duración del bloqueo que informó el túnel, si la hay. */
  private handleError(msg: string, retryAfterMs?: number): void {
    const code = msg as PlayerErrorCode;
    throttledLogError(
      `${this.idCamara}:${this.canal}:${msg}`,
      '[camera-player]',
      msg,
      `(cámara ${this.idCamara} canal ${this.canal})`,
    );

    this.closePeer();

    // Errores transitorios (saturación/timeout/ICE): reintento con backoff
    // exponencial + jitter. Los permanentes (sin señal, códec) no se
    // reintentan — reintentar contra un DVR que da 404 solo genera flood.
    if (
      RETRYABLE_ERRORS.has(code) &&
      this.retryCount < CameraPlayerComponent.MAX_RETRIES
    ) {
      const delay = 1500 * 2 ** this.retryCount + Math.random() * 500;
      this.retryCount++;
      this.loading = true;
      this.errorMsg = null;
      this.clearCountdown();
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.retryTimer = setTimeout(() => void this.load(), delay);
      return;
    }

    // El cooldown dura lo que dice el túnel (así se reintenta justo cuando
    // levanta su bloqueo), o OFFLINE_COOLDOWN_MS si no dijo nada.
    const cooldownMs = retryAfterMs ?? OFFLINE_COOLDOWN_MS;
    if (COOLDOWN_ERRORS.has(code) && !this.fecha) {
      offlineUntil.set(`${this.idCamara}:${this.canal}`, {
        until: Date.now() + cooldownMs,
        code,
      });
    }

    this.errorMsg = ERROR_MESSAGES[msg] ?? msg;
    this.puedeReintentar =
      RETRYABLE_ERRORS.has(code) || COOLDOWN_ERRORS.has(code);
    this.loading = false;
    this.errorEmiter.emit(msg);

    // Seguir intentando en segundo plano salvo errores que no cambian solos
    // (códec/navegador) y playback (una grabación que falla no se "arregla").
    if (this.puedeReintentar && !this.fecha) {
      this.scheduleSlowRetry(COOLDOWN_ERRORS.has(code) ? cooldownMs : 0);
    }
  }

  /** Programa el próximo reintento lento: el mayor entre el escalón de
   * backoff y `minMs` (fin del cooldown), +0-30% de jitter. Si la pestaña está
   * oculta espera a que vuelva a estar visible. */
  private scheduleSlowRetry(minMs: number): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.clearCountdown();
    const steps = CameraPlayerComponent.SLOW_RETRY_MS;
    const step = steps[Math.min(this.slowRetryCount, steps.length - 1)];
    this.slowRetryCount++;
    const base = Math.max(step, minMs);
    const delay = base + Math.random() * base * 0.3;
    const at = Date.now() + delay;

    this.retryInSecs = Math.ceil(delay / 1000);
    this.countdownTimer = setInterval(() => {
      this.retryInSecs = Math.max(0, Math.ceil((at - Date.now()) / 1000));
    }, 1000);

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.clearCountdown();
      if (document.hidden) {
        this.onVisible = () => {
          if (document.hidden) return;
          this.removeVisibilityListener();
          void this.load();
        };
        document.addEventListener('visibilitychange', this.onVisible);
        return;
      }
      void this.load();
    }, delay);
  }

  /** Video congelado: sin frames nuevos durante STALL_MS con la pestaña
   * visible → recargar (go2rtc reabre la sesión si hace falta). */
  private startStallWatch(): void {
    this.stopStallWatch();
    this.lastFrames = -1;
    this.lastFrameAt = Date.now();
    this.stallTimer = setInterval(() => {
      const video = this.videoEl?.nativeElement;
      if (!video || document.hidden) {
        this.lastFrameAt = Date.now();
        return;
      }
      const frames =
        video.getVideoPlaybackQuality?.().totalVideoFrames ??
        Math.floor(video.currentTime * 1000);
      if (frames !== this.lastFrames) {
        this.lastFrames = frames;
        this.lastFrameAt = Date.now();
        return;
      }
      if (Date.now() - this.lastFrameAt > CameraPlayerComponent.STALL_MS) {
        this.stopStallWatch();
        this.handleError('STREAM_STALLED');
      }
    }, 3_000);
  }

  private stopStallWatch(): void {
    if (this.stallTimer) {
      clearInterval(this.stallTimer);
      this.stallTimer = null;
    }
  }

  private clearCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.retryInSecs = null;
  }

  private removeVisibilityListener(): void {
    if (this.onVisible) {
      document.removeEventListener('visibilitychange', this.onVisible);
      this.onVisible = null;
    }
  }

  /** Cierra solo la RTCPeerConnection (no cancela reintentos programados).
   * `park`: si es vivo, la deja en espera en livePeerPool en vez de cerrarla. */
  private closePeer(park = false): void {
    this.stopStallWatch();
    if (this.disconnectedTimer) {
      clearTimeout(this.disconnectedTimer);
      this.disconnectedTimer = null;
    }
    if (this.videoEl?.nativeElement) {
      this.videoEl.nativeElement.srcObject = null;
    }
    if (this.pc && park && this.peerKey && this.mediaStream) {
      livePeerPool.park(this.peerKey, {
        pc: this.pc,
        stream: this.mediaStream,
      });
      this.pc = null;
    }
    if (this.pc) {
      try {
        this.pc.close();
      } catch {
        /* ignore */
      }
      this.pc = null;
    }
    this.mediaStream = null;
    this.peerKey = null;
  }

  /** `park`: destrucción o cambio de inputs; la conexión en vivo queda en
   * espera unos segundos por si se vuelve a la misma cámara. */
  stop(park = false): void {
    this.loadSeq++;
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.clearCountdown();
    this.removeVisibilityListener();
    this.closePeer(park);
  }

  togglePTZ(): void {
    this.ptzVisible = !this.ptzVisible;
  }

  startPTZ(operation: number): void {
    if (this.ptzBusy) return;
    this.ptzBusy = true;
    void this.executePTZ(operation);
    this.ptzInterval = setInterval((): void => {
      void this.executePTZ(operation);
    }, 200);
  }

  async stopPTZ(): Promise<void> {
    const wasActive = this.ptzInterval !== null || this.ptzBusy;
    if (this.ptzInterval) {
      clearInterval(this.ptzInterval);
      this.ptzInterval = null;
    }
    if (wasActive && this.idCamara && this.ptzSupported) {
      await this.executePTZ(10);
    }
    this.ptzBusy = false;
  }

  onPtzTouchStart(event: TouchEvent, operation: number): void {
    event.preventDefault();
    this.startPTZ(operation);
  }

  private async executePTZ(operation: number): Promise<void> {
    if (!this.idCamara) return;
    try {
      const response = await this.camarasService.movePTZ(
        this.idCamara,
        this.canal ?? '0',
        operation,
      );
      if (response && (response as { ok?: boolean }).ok === false) {
        this.ptzSupported = false;
        this.ptzVisible = false;
        this.ptzBusy = false;
        if (this.ptzInterval) {
          clearInterval(this.ptzInterval);
          this.ptzInterval = null;
        }
      }
    } catch (err) {
      console.warn('PTZ error:', err);
      this.ptzBusy = false;
    }
  }

  botonClick(): void {
    this.maximizadoEmiter.emit(true);
  }
}
